import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState, lazy } from 'react'
import {
  faBolt,
  faChevronDown,
  faHeading,
  faLayerGroup,
  faPlus,
} from '@fortawesome/free-solid-svg-icons'
import { Button } from '@/components/button'

import KeyboardNavigationHandler from '@/components/rundown/KeyboardNavigationHandler.jsx'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import _debounce from 'lodash/debounce'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  collapsedGroupsAtom,
  setCueOrderAtom,
  collapseGroupAtom,
  rundownAtom,
  cuesAtom,
} from '../../store/rundown.store.ts'
import UpgradeMessage from '@/components/UpgradeMessage.jsx'
import {
  CUE_TYPE_CUE,
  CUE_TYPE_GROUP,
  CUE_TYPE_HEADING,
} from '../../constants/cue-types.ts'
import { CUE_BACKGROUND_COLORS } from '@rundown-studio/consts'
import { Menu, MenuItem } from '@/components/interactives/dropdown-menu.tsx'
import RundownDayTZ from '@/components/rundown/timing/RundownDayTZ.jsx'
import OverUnder from '@/components/rundown/timing/OverUnder.jsx'
import EndOfShow from '@/components/rundown/timing/EndOfShow.jsx'
import BatchEditToolbar from '@/components/rundown/batch-edit-toolbar.tsx'
import PlannedEnd from '@/components/rundown/timing/PlannedEnd.jsx'
import {
  clearDragOverAtom,
  dragLeaveAtom,
  isDraggingAtom,
  CueDropzone,
  getIsGrouping,
  reorderCues,
  moveCueToGroup,
  getCueDropzone,
  getTargetCueId,
} from '../cues-list/index.ts'
import {
  useGroupRundownCues,
  useReorderRundownCues,
  useAddRundownCue,
} from '../../entities/rundown/index.ts'
import CuesList from '../cues-list/cues-list.js'
import { RundownPermissionGuard } from '../permissions/ui/rundown-permission-guard.tsx'
import { useModal } from '../modal-manager/index.ts'
import { RundownPermission } from '@rundown-studio/types'
import { useRundownPermission } from '../permissions/lib/use-rundown-permission.ts'
import { runnerAtom } from '@/store/runner.store.ts'
import { getIsCuesLimitExceededAtom } from '@/store/plan.store.ts'
import { Grid } from './grid/Grid.tsx'
import { UpgradeModalAdapter } from './modals/UpgradeModal.tsx'
import { ModalManager } from './modals/ModalManager.tsx'
import { RundownShortcutsHandler } from './shortcuts/RundownShortcutsHandler.tsx'

const RundownColoursModal = lazy(
  () => import('@/components/modal/RundownColoursModal.jsx'),
)
const UpgradeModal = lazy(() => import('@/components/modal/UpgradeModal.jsx'))

export function RundownBody() {
  const rundown = useAtomValue(rundownAtom)
  const cues = useAtomValue(cuesAtom)

  const runner = useAtomValue(runnerAtom)

  const writeAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  const readonly = !writeAccess

  const { rundownId } = useParams()
  const [cueCountToAdd, setCueCountToAdd] = useState(1)

  const [coloursModal, renderColoursModal] = useModal(RundownColoursModal)
  const [upgradeModal, renderUpgradeModal] = useModal(UpgradeModal)

  // Jotai atoms
  const setCueOrder = useSetAtom(setCueOrderAtom)
  const getIsCuesLimitExceed = useSetAtom(getIsCuesLimitExceededAtom)
  const collapsedGroups = useAtomValue(collapsedGroupsAtom)

  // Drag and drop atoms
  const isDragging = useAtomValue(isDraggingAtom)
  const clearDragOver = useSetAtom(clearDragOverAtom)
  const dragLeave = useSetAtom(dragLeaveAtom)
  const collapseGroup = useSetAtom(collapseGroupAtom)

  const { mutate: addRundownCue, isPending: isAddingRundownCue } =
    useAddRundownCue()
  const { mutate: groupRundownCues, isPending: isGroupingRundownCues } =
    useGroupRundownCues()
  const { mutate: reorderRundownCues, isPending: isReorderingCues } =
    useReorderRundownCues()
  // const { measure } = useCuesListVirtualizer()

  const isLoading =
    isReorderingCues || isGroupingRundownCues || isAddingRundownCue

  // Debounced clear drag over function
  const clearDraggedOverCue = useMemo(
    () => _debounce(() => clearDragOver(), 150),
    [clearDragOver],
  )

  const isCuesLimitExceeded = getIsCuesLimitExceed(cueCountToAdd)

  const cueTypesTitleMap = {
    [CUE_TYPE_CUE]: {
      title: 'New cue',
      colour: '',
    },
    [CUE_TYPE_HEADING]: {
      title: 'New heading',
      backgroundColor: 'transparent',
    },
    [CUE_TYPE_GROUP]: {
      title: 'New group',
      backgroundColor: CUE_BACKGROUND_COLORS[6],
    },
  }

  async function handleAddCue(type = CUE_TYPE_CUE) {
    // Check user limits
    if (isCuesLimitExceeded) {
      return upgradeModal.show({
        message: "You've reached your limit for cues on this rundown.",
      })
    }

    addRundownCue(
      {
        rundownId,
        params: {
          count: cueCountToAdd,
        },
        payload: {
          type,
          title: cueTypesTitleMap[type].title,
          backgroundColor: cueTypesTitleMap[type].backgroundColor,
        },
      },
      {
        onSuccess() {
          setCueCountToAdd(1)
        },
      },
    )
  }

  const onDragEnd = useCallback(
    ({ active, over }) => {
      if (!active || !over) return

      const sourceCueId = active.id

      const targetCueId = getTargetCueId(over.id)
      const dropzone = getCueDropzone(over.id)
      const isGroupOperation = getIsGrouping(over.id)

      const previousCuesOrder = structuredClone(rundown.cues)

      if (isGroupOperation) {
        // DroppablePosition.CUE means dropping onto another cue
        if (dropzone === CueDropzone.CUE) {
          // Create new group
          groupRundownCues(
            {
              rundownId,
              beforeCueId: targetCueId,
              cuesToGroupIds: [targetCueId, sourceCueId],
            },
            {
              onSuccess() {
                // measure()
              },
            },
          )
        } else {
          // Add to existing group
          const updatedCues = moveCueToGroup(
            previousCuesOrder,
            sourceCueId,
            targetCueId,
          )

          setCueOrder(updatedCues)

          reorderRundownCues(
            { rundownId, cues: updatedCues },
            {
              onError() {
                setCueOrder(previousCuesOrder)
              },
            },
          )
        }
      } else {
        const updatedCues = reorderCues(
          previousCuesOrder,
          sourceCueId,
          targetCueId,
          dropzone === CueDropzone.AFTER,
        )

        setCueOrder(updatedCues)

        reorderRundownCues(
          { rundownId, cues: updatedCues },
          {
            onError() {
              setCueOrder(previousCuesOrder)
            },
          },
        )
      }
    },
    [
      groupRundownCues,
      reorderRundownCues,
      rundown.cues,
      rundownId,
      setCueOrder,
    ],
  )

  const onDragLeave = useCallback(() => {
    if (!isDragging) return
    dragLeave()
    clearDraggedOverCue()
  }, [isDragging, dragLeave, clearDraggedOverCue])

  /**
   * Expand a collapsed group if it contains the "next" cue
   */
  useEffect(() => {
    if (!runner) return
    if (runner.timesnap.running && runner.nextCueId) {
      // const nextCuePartentId = _find(cues, {id: runner.nextCueId})
      const filteredByCollapsedGroups = rundown.cues.filter((cue) =>
        cue.children?.find((child) => child.id === runner.nextCueId),
      )
      if (
        filteredByCollapsedGroups.length &&
        collapsedGroups.includes(filteredByCollapsedGroups[0]?.id)
      )
        collapseGroup(filteredByCollapsedGroups[0]?.id)
    }
  }, [collapseGroup, collapsedGroups, rundown.cues, runner])

  return (
    <div
      className={[
        'min-w-max w-full flex flex-col gap-0',
        runner?.timesnap?.running ? 'pb-[70vh]' : 'pb-20',
      ].join(' ')}
      onDragLeave={onDragLeave}
    >
      <div className="mx-8 my-2 relative">
        <BatchEditToolbar
          rundownId={rundownId}
          cues={cues}
          cueOrder={rundown.cues}
          setRundownColoursModalOpen={coloursModal.show}
        />
        <RundownDayTZ timezone={rundown.timezone} readonly={readonly} />
      </div>

      <Grid>
        <CuesList onDragEnd={onDragEnd} />
      </Grid>

      {/* Plan limit alert */}
      <div>
        {isCuesLimitExceeded && (
          <div className="m-6">
            <UpgradeMessage message="Cues limit exceeded for this rundown. Upgrade your account to add and edit more cues." />
          </div>
        )}
      </div>

      <div className="ml-10 mb-5">
        <div className="inline-flex items-center gap-8 bg-[#161616] rounded-sm px-4 py-3 text-base">
          <PlannedEnd rundown={rundown} />
          <EndOfShow
            timezone={rundown.timezone}
            todDisplayFormat={rundown.settings?.todDisplayFormat}
          />
          <OverUnder />
        </div>
      </div>

      <RundownPermissionGuard permission={RundownPermission.EDIT}>
        <div className="ml-10 flex items-center">
          <div className="flex">
            <Button
              className="text-base font-normal! rounded-r-none tabular-nums"
              text={`Add ${cueCountToAdd > 1 ? cueCountToAdd : ''} cue${cueCountToAdd > 1 ? 's' : ''}`}
              icon={faPlus}
              loading={isLoading}
              colour="dark"
              toolTip={
                isCuesLimitExceeded ? (
                  <>
                    Limit reached
                    <FontAwesomeIcon
                      icon={faBolt}
                      className="text-yellow-500"
                    />{' '}
                    Upgrade for more
                  </>
                ) : (
                  ''
                )
              }
              onClick={() => handleAddCue()}
              data-label="create-rundown-button"
            />
            <Menu
              className="rounded-l-none border-l-2 border-gray-800"
              icon={faChevronDown}
            >
              <MenuItem
                icon={faHeading}
                label="Add heading"
                onClick={() => handleAddCue(CUE_TYPE_HEADING)}
              />
              <MenuItem
                icon={faLayerGroup}
                label="Add group"
                onClick={() => handleAddCue(CUE_TYPE_GROUP)}
              />
            </Menu>
          </div>
          <label htmlFor="addCuesInput" className="ml-2 text-sm space-x-1">
            <input
              id="addCuesInput"
              className="px-2 h-9 w-16 bg-transparent focus:outline-hidden focus:ring-3 rounded-sm border border-white/20"
              value={cueCountToAdd}
              onChange={(e) => {
                const min = 1
                const max = 30
                let _val = 1
                if (e.target.value < min) {
                  _val = 1
                } else if (e.target.value > max) {
                  _val = max
                } else {
                  _val = parseInt(e.target.value)
                }
                setCueCountToAdd(_val)
              }}
              type="number"
              step={1}
              min={1}
              max={30}
            />
            <span className="h-7 inline-block text-gray-600">
              more cue
              {cueCountToAdd > 1 && 's'}
            </span>
          </label>
        </div>
      </RundownPermissionGuard>

      <RundownShortcutsHandler />
      <KeyboardNavigationHandler />

      {renderColoursModal({
        colours: rundown.settings || {
          cueBackgroundColours: CUE_BACKGROUND_COLORS,
        },
        rundownId,
      })}
      {renderUpgradeModal({
        message: '',
      })}

      <ModalManager />

      {/* Temp: New modal structure */}
      <UpgradeModalAdapter />
    </div>
  )
}
